import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';

import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, ButtonWidget, FlyoutListMenuWidget, HorizontalOriginOption, InteractionState, MenuItemInfo, VerticalOriginOption } from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { DeprecatedReactMenu } from '../../common/react/components/Menu';
import { Location } from '../../common/react/Location';
import { UserActions } from '../../common/react/UserActions';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { UserSettingsActions } from '../../ts/actions/UserSettingsActions';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { SiteRolesResource } from '../../ts/resources/SiteRoles';
import { AuthenticationType } from '../../ts/ServerApi/types/IServerSettingsUnauthenticated';
import { ServerService } from '../../ts/services/ServerService';
import { Telemetry } from '../../ts/services/TelemetryService';
import { ProfilePictureStyles } from '../content/styles/ProfilePictureStyles';
import { UserProfilePicture } from '../content/UserProfilePicture';
import { NavLocationSelectors } from '../selectors/NavigationStateSelectors';
import {
  selectSessionUser, SessionServerSelectors,
  SessionSiteSelectors, SessionUserSelectors,
} from '../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../selectors/SiteUrlsSelectors';
import { ServerUrls } from '../ServerUrls';
import { ServiceLocator } from '../ServiceLocator';
import { AppState } from '../state/AppState';
import { MapStateToProps } from '../utils/ConnectUtils';
import { HeaderStyles } from './HeaderStyles';

type UserProfilePictureButtonProps = UserProfilePicture.Props & DeprecatedReactMenu.ButtonProps;

export class UserProfilePictureButton extends React.Component<UserProfilePictureButtonProps> {
  public render(): JSX.Element {
    return <UserProfilePicture {...this.props} />;
  }
}

export interface UserMenuOwnProps {
  readonly style?: any;

  // The "in*area" props should be state props, but the old top bar computed them differently
  // than the current implementation does. We could get these props directly from the UserMenu
  // connected component instead of passing them in from ConnectedPageHeaderMenus.
  readonly inServerArea: boolean;
  readonly inSiteContentArea: boolean;
  readonly telemetryService: Telemetry;
}

export interface UserMenuPropsFromReduxState {
  readonly siteUrls: SiteUrls;
  readonly user: IUserInfo;
  readonly isLogoutEnabled: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSiteAdmin: boolean;
  readonly isOnlineEnvironment: boolean;
  readonly isSingleSiteServerAdmin: boolean;
  readonly isInVizViewerArea: boolean;
}

export interface UserMenuPropsFromAngular {
  readonly userActions: UserActions;
  readonly authenticationType: AuthenticationType;
}

export interface UserMenuState {
  readonly isMenuOpen: boolean;
}

export type UserMenuViewComponentProps = UserMenuOwnProps & UserMenuPropsFromReduxState & UserMenuPropsFromAngular;

const buttonStyles: React.CSSProperties = {
  cursor: 'pointer'
};

const profilePictureMarginSize = (HeaderStyles.TopBarHeight - ProfilePictureStyles.Sizes.ExtraSmallIconSize) / 2;
const divAroundButtonStyle: React.CSSProperties = { paddingRight: '10px', marginTop: profilePictureMarginSize, marginBottom: profilePictureMarginSize, marginRight: '-2px'};


export class UserMenuViewComponent extends React.Component<UserMenuViewComponentProps, UserMenuState> {
  private buttonEl: HTMLElement | undefined;
  private serverUrls: ServerUrls;
  static signOut = 'signOut';
  static myAccountServer = 'myAccountSettingsServer';
  static myAccountSettings = 'myAccountSettings';
  static myContent = 'myContent';
  static myContentSiteOnly = 'myContentSiteOnly';
  static userStartPageChange = 'userStartPageChange';
  static defaultStartPageChange = 'defaultStartPageChange';
  static startPageChangeSubMenu = 'StartPageChangeSubMenu';
  static siteStartPageChange = 'siteStartPageChange';

  constructor(props: UserMenuViewComponentProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
    };
    this.serverUrls = ServerUrls.getInstance();
  }

  private close = (): void => {
    this.setState({ isMenuOpen: false });
  }

  private toggleMenuOpen = () => {
    this.props.telemetryService.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'user-menu',
      action: this.state.isMenuOpen ? 'close' : 'open'
    });
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  }

  private isOnlineSiteAdmin(): boolean {
    return this.props.isSiteAdmin && this.props.isOnlineEnvironment;
  }

  private isMultiSiteServerAdmin(): boolean {
    return this.props.isSessionServerAdmin && !this.props.isSingleSiteServerAdmin;
  }

  private get isUserAdmin(): boolean {
    return this.props.isSiteAdmin || this.props.isSessionServerAdmin;
  }

  private handleButtonRef = (ref: ButtonWidget) => {
    this.buttonEl = ref ? ReactDOM.findDOMNode(ref) as HTMLElement : undefined;
  }

  private handleSelect = (value: string) => {
    switch (value) {
      case UserMenuViewComponent.signOut: {
        this.props.userActions.signout();
        break;
      }
      case UserMenuViewComponent.myAccountServer: {
        Location.instance.setUrl(this.serverUrls.userSettingsUrl(this.props.user.domainName, this.props.user.username));
        break;
      }
      case UserMenuViewComponent.myAccountSettings: {
        Location.instance.setUrl(this.props.siteUrls.userSettingsUrl(this.props.user.username, this.props.user.domainName));
        break;
      }
      case UserMenuViewComponent.myContent: {
        Location.instance.setUrl(this.props.siteUrls.userContentUrl(this.props.user.username, this.props.user.domainName));
        break;
      }
      case UserMenuViewComponent.myContentSiteOnly:
      case UserMenuViewComponent.userStartPageChange: {
        this.props.userActions.changeUserStartPage();
        break;
      }
      case UserMenuViewComponent.defaultStartPageChange: {
        this.props.userActions.changeServerDefaultStartPage();
        break;
      }
      case UserMenuViewComponent.siteStartPageChange: {
        this.props.userActions.changeSiteStartPage();
      }
    }

    this.close();
  }

  private getButtonContent = (state: InteractionState): JSX.Element => {
    let divStyle: React.CSSProperties = {border: '1px solid transparent'};
    return (<UserProfilePicture
              profileDisplayName={this.props.user ? this.props.user.displayName : ''}
              imageUrl={this.props.user ? this.props.user.userImageUrl : ''}
              imageSize={ProfilePictureStyles.Sizes.ExtraSmallIconSize}
              fontSize={ProfilePictureStyles.FontSizes.ExtraSmallIconFontSize}
              divStyle={divStyle}
              useInvertedColors={this.props.isInVizViewerArea}/>);
  }

  //TODO: Update https://opengrok/source/xref/teams_near/product-tests/java/libraries/VizportalUILib/src/com/tableausoftware/test/server/vizportalUILib/pageObjects/components/UserMenu.java#37
  render() {
    let menuItemInfos: MenuItemInfo[] = [];

    if (this.props.inServerArea) {
      menuItemInfos.push({
        value: UserMenuViewComponent.myContentSiteOnly,
        displayValue: Messages.myContentSiteOnly(),
        disabled: true,
      });

      menuItemInfos.push({
        value: UserMenuViewComponent.myAccountServer,
        displayValue: Messages.myAccountSettingsServer()
      });
    } else {
      menuItemInfos.push({
        value: UserMenuViewComponent.myContent,
        displayValue: Messages.myContent()
      });
      menuItemInfos.push({
        value: UserMenuViewComponent.myAccountSettings,
        displayValue: Messages.myAccountSettings()
      });
    }

    // Change start page

    const submenuIsEnabled = this.props.inSiteContentArea || this.isUserAdmin;
    const submenuSiteStartPageEnabled = this.props.inSiteContentArea;
    if (this.props.isSessionServerAdmin || this.isOnlineSiteAdmin()) {

      let subMenuItemInfos: MenuItemInfo[] = [];

      subMenuItemInfos.push({
        value: UserMenuViewComponent.userStartPageChange,
        displayValue: Messages.defaultStartPageForMe(),
      });
      if (FeatureFlags.siteDefaultStartPage &&
        (this.isOnlineSiteAdmin() ||
          this.isMultiSiteServerAdmin())) {
        subMenuItemInfos.push({
          value: UserMenuViewComponent.siteStartPageChange,
          displayValue: Messages.defaultStartPageForSite(),
          disabled: !submenuSiteStartPageEnabled
        });
      }
      if (this.props.isSessionServerAdmin) {
        subMenuItemInfos.push({
          value: UserMenuViewComponent.defaultStartPageChange,
          displayValue: Messages.defaultStartPageForEveryone(),
          disabled: !this.props.inSiteContentArea
        });
      }
      menuItemInfos.push({
        value: UserMenuViewComponent.startPageChangeSubMenu,
        displayValue: Messages.defaultStartPageSubMenu(),
        menuItemInfos: subMenuItemInfos,
        title: submenuIsEnabled ? '' : Messages.defaultStartPageRestriction(),
        disabled: !submenuIsEnabled,
        subMenuStyle: {fontWeight: 'normal'}
      });
    } else {
      menuItemInfos.push({
        value: UserMenuViewComponent.userStartPageChange,
        displayValue: Messages.userStartPageChange(),
      });
    }

    // Logout

    if (this.props.isLogoutEnabled) {
      const switchUser = this.props.authenticationType === AuthenticationType.Sspi ||
        this.props.authenticationType === AuthenticationType.Kerberos;
      menuItemInfos.push({
        value: UserMenuViewComponent.signOut,
        displayValue: switchUser ? Messages.switchUser() : Messages.signOut(),
      });
    }

    return (
      <>
        <div style={divAroundButtonStyle}>
          <ButtonWidget
            buttonType={ButtonType.IconOutline}
            handleClick={this.toggleMenuOpen}
            ref={this.handleButtonRef}
            testId="flyout-list-menu"
            style={buttonStyles}
            getContent={this.getButtonContent}
            title={Messages.userMenuButton()}
          />
        </div>
        {this.state.isMenuOpen &&
          <FlyoutListMenuWidget
            menuStyle={{ fontWeight: 'normal' }}
            allowAutoPosition
            anchorEl={this.buttonEl}
            anchorOrigin={{
              vertical: VerticalOriginOption.bottom,
              horizontal: HorizontalOriginOption.left,
            }}
            menuItemInfos={menuItemInfos}
            onSelect={this.handleSelect}
            requestCloseAll={this.close}
            testId="flyout-list-menu"
            toggleEl={this.buttonEl}
          />
        }
      </>
    );
  }
}

export namespace UserMenuAngularProvider {
  interface AngularServices {
    serverService: ServerService;
    userActions: UserActions;
  }

  function getAngularServicesWithServiceLocator() {

    const serverService = ServiceLocator.get<ServerService>('ServerService');
    const userSettingsActions = ServiceLocator.get<UserSettingsActions>('UserSettingsActions');

    return {
      serverService,
      userActions: new UserActions(serverService, userSettingsActions),
    };
  }

  function selectPropsFromAngularServices(angularServices: AngularServices) {
    return {
      userActions: angularServices.userActions,
      authenticationType: angularServices.serverService.authenticationType(),
    };
  }

  export const HOC = angularProviderHOC<UserMenuOwnProps, UserMenuPropsFromAngular, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

export namespace ReduxConnectedUserMenu {

  const mapStateToProps: MapStateToProps<UserMenuPropsFromReduxState> = (state: AppState) => ({
    siteUrls: siteUrlsSelector(state),
    user: selectSessionUser(state),
    isLogoutEnabled: SessionSiteSelectors.isLogoutEnabled(state),
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(state),
    isSiteAdmin: SiteRolesResource.isSiteAdmin(SessionSiteSelectors.siteRole(state)),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
    isSingleSiteServerAdmin: SessionUserSelectors.isSingleSiteServerAdmin(state),
    isInVizViewerArea: NavLocationSelectors.inVizViewerArea(state),
  });

  export const HOC = connect(mapStateToProps);
}

export const  UserMenu: React.ComponentType<UserMenuOwnProps> =
  UserMenuAngularProvider.HOC(
    ReduxConnectedUserMenu.HOC(
      UserMenuViewComponent
    )
  );
